<script setup lang="ts">
import { useField, useForm } from 'vee-validate';
import { computed, ref } from 'vue';
import * as yup from 'yup';

import BaseButton from '@/components/base/BaseButton.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import { constant } from '@/constant';
import { useUserStore } from '@/store/userStore';
import { getLabel } from '@/utils/data-display-utils';
import { Locales } from '@/store/language.interface';

// REFS
const emailInputRef = ref();

// STORE
const userStore = useUserStore();

// PROPS
const props = defineProps<{
  emailAlreadyInList: boolean;
  emailNeedsVerification: boolean;
  inputFilled?: boolean;
  userSubscribed: boolean;
  languageEnabled?: Locales[];
}>();

// EMITS
const emit = defineEmits<{
  (e: 'subscribe', values: { email: string }): void;
  (e: 'unsubscribe'): void;
}>();

// FORM VALIDATION
const schema = yup.object({
  email: yup.string().when([], {
    is: () => userLoggedIn.value,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.matches(constant.EmailRegex).required(),
  }),
});

const { handleSubmit, meta, setFieldValue } = useForm({
  initialValues: { email: null },
  validationSchema: schema,
});

const { value: emailField } = useField<string | null>('email');

// FUNCTIONS
const subscribe = handleSubmit(async (values) => {
  emit('subscribe', values);
});

const unsubscribe = () => {
  emit('unsubscribe');
};

function resetForm() {
  emailField.value = null;
  setFieldValue('email', null);
  emailInputRef.value?.clearValue();
}

function updateEmailInput(email: string) {
  setFieldValue('email', email);
  emailField.value = email;
}

defineExpose({
  resetForm,
});

// COMPUTED
const userLoggedIn = computed(() => {
  return userStore.isUserLogged;
});
</script>
<template>
  <div class="user-subscribe">
    <div class="buttons">
      <BaseInput
        v-if="!userLoggedIn"
        :model="emailField"
        @update="updateEmailInput"
        type="email"
        :placeholder="getLabel('subscribe.email-placeholder', props.languageEnabled)"
        name="email"
        ref="emailInputRef"
        class="email-input"
        :filled="props.inputFilled"
      />
      <BaseButton
        v-if="!props.userSubscribed"
        :text="getLabel('subscribe.button.subscribe', props.languageEnabled)"
        @click="subscribe"
        primary
        :disabled="!userLoggedIn && !meta.valid"
        class="subscribe-button"
      />
      <BaseButton
        v-if="props.userSubscribed && userLoggedIn"
        :text="getLabel('subscribe.button.unsubscribe', props.languageEnabled)"
        @click="unsubscribe"
        primary
        class="unsubscribe-button"
      />
    </div>
    <div class="status">
      <span v-if="props.emailAlreadyInList">
        {{ getLabel('subscribe.status.already-on-email-list', props.languageEnabled) }}
      </span>
      <span v-else-if="props.emailNeedsVerification">
        {{ getLabel('subscribe.status.need-email-verification', props.languageEnabled) }}
      </span>
    </div>
  </div>
</template>
<style lang="scss">
.user-subscribe {
  .buttons {
    display: flex;
    flex-direction: row;
    gap: $space-md;

    .q-field {
      width: 60%;
    }
  }
}
</style>
